import { useCallback } from 'react';

import { ToothNumeration } from '@/shared/api/protocol-ts/model/dto_report_common_pb';
import { PendingChange } from '@/shared/config';
import { useAppDispatch, useAppSelector } from '@/shared/hooks';

import { organizationModel } from '@/entities/organization';
import { reportsModel, useCheckReportSignature } from '@/entities/reports';
import { toolbarMIRModel } from '@/entities/toolbarMIR';
import { toothModel, ToothWithDetections } from '@/entities/tooth';

import { toothNumberEditModel } from '@/features/toothNumberEdit';

export const useToothNumberEditing = () => {
  const dispatch = useAppDispatch();
  const { checkReportSignature } = useCheckReportSignature();

  const selectedTooth = useAppSelector(
    toothNumberEditModel.selectors.selectSelectedTooth,
  ) as ToothWithDetections | undefined;

  const pendingChanges = useAppSelector(
    toothNumberEditModel.selectors.selectPendingChanges,
  );

  const isEditingToothNumbers = useAppSelector(
    toolbarMIRModel.selectors.selectIsEditingTeeth('main'),
  );
  const teeth = useAppSelector(toothModel.selectors.selectForCurrentReport);
  const dentalNotationFormat = useAppSelector(
    organizationModel.selectors.selectDentalNotationFormat,
  );

  const selectTooth = useCallback((tooth?: ToothWithDetections) => {
    dispatch(toothNumberEditModel.actions.setSelectedTooth(tooth ?? null));
  }, []);

  const handleChangeToothNumber = useCallback(
    (tooth: ToothWithDetections, newNumber: number) => {
      const currentToothDetection = teeth
        .find((t) => t.ID === tooth.toothID)
        ?.Detections.find((d) => d.ID === tooth.detectionID);
      if (!currentToothDetection) {
        return;
      }

      const newChange: PendingChange = {
        tooth,
        newNumber,
        SupernumeraryIndex: 0,
      };

      dispatch(toothNumberEditModel.actions.addPendingChange(newChange));

      dispatch(toothNumberEditModel.actions.setSelectedTooth(null));
    },
    [teeth],
  );

  const handleCloseEditMode = useCallback(() => {
    dispatch(
      toolbarMIRModel.actions.activateTool({
        toolbarID: 'main',
        control: 'editNumbers',
        value: false,
      }),
    );
  }, []);

  const handleConfirmChanges = useCallback(() => {
    checkReportSignature({
      onSignatureChecked: async () => {
        try {
          const changes = pendingChanges.map((change: PendingChange) => {
            const numeration = new ToothNumeration();
            numeration.ISO = change.newNumber;
            numeration.SupernumeraryIndex = change.SupernumeraryIndex;

            return dispatch(
              reportsModel.thunks.setToothNumerationAndRevive({
                ToothID: change.tooth.toothID,
                Numeration: numeration,
              }),
            );
          });

          await Promise.all(changes);

          dispatch(toothNumberEditModel.actions.clearPendingChanges());
          handleCloseEditMode();
        } catch (error) {
          console.error('Error applying teeth number changes:', error);
        }
      },
    });
  }, [pendingChanges, handleCloseEditMode, checkReportSignature]);

  const handleRemoveTooth = useCallback(
    (tooth: ToothWithDetections) => {
      checkReportSignature({
        onSignatureChecked: async () => {
          dispatch(toothNumberEditModel.actions.addPendingRemove(tooth));
          dispatch(toothNumberEditModel.actions.setSelectedTooth(null));
        },
      });
    },
    [checkReportSignature],
  );

  return {
    selectedTooth,
    pendingChanges,
    isEditingToothNumbers,
    dentalNotationFormat,
    selectTooth,
    handleChangeToothNumber,
    handleConfirmChanges,
    handleRemoveTooth,
    handleCloseEditMode,
  };
};
